import { useState, useEffect, useCallback } from 'react'
import { Brain, RefreshCw, Search, ExternalLink, BookOpen } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const INDUSTRIES = [
  { value: 'all', label: 'All Industries' },
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail' },
  { value: 'government', label: 'Government' },
  { value: 'education', label: 'Education' },
  { value: 'legal', label: 'Legal' },
  { value: 'energy', label: 'Energy' },
]

const INDUSTRY_BADGE: Record<string, string> = {
  financial_services: 'badge-blue',
  healthcare: 'badge-green',
  manufacturing: 'badge-amber',
  retail: 'badge-purple',
  government: 'badge-cyan',
  education: 'badge-blue',
  legal: 'badge-amber',
  energy: 'badge-red',
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function AIAdoptionPage() {
  const [items, setItems] = useState<any[]>([])
  const [industryFilter, setIndustryFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = { limit: 50 }
      if (industryFilter !== 'all') params.industry = industryFilter
      const { data } = await api.get('/api/ai/feed', { params })
      setItems(data.items || [])
    } finally {
      setLoading(false)
    }
  }, [industryFilter])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const filtered = items.filter(item => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      if (
        !(item.title || '').toLowerCase().includes(q) &&
        !(item.summary || '').toLowerCase().includes(q)
      ) return false
    }
    return true
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Brain size={24} className="text-purple" /> AI Adoption Intelligence
          </h1>
          <p className="text-sm text-text-secondary mt-1">Enterprise AI adoption signals from VentureBeat, MIT Tech Review, ArsTechnica, and industry reports</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search AI adoption news..." className="input pl-9 py-2 text-xs" />
        </div>
        <select title="Filter by industry" value={industryFilter} onChange={e => setIndustryFilter(e.target.value)} className="input w-auto py-2 text-xs">
          {INDUSTRIES.map(ind => (
            <option key={ind.value} value={ind.value}>{ind.label}</option>
          ))}
        </select>
      </div>

      {loading && !items.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching AI adoption feed...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => (
          <div key={item.id || idx} className="card card-hover p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {item.industry && (
                    <span className={clsx('text-[10px]', INDUSTRY_BADGE[item.industry] || 'badge-blue')}>{item.industry.replace(/_/g, ' ')}</span>
                  )}
                  {item.use_case && (
                    <span className="badge-purple text-[10px]">{item.use_case.replace(/_/g, ' ')}</span>
                  )}
                  {item.is_regulation && (
                    <span className="badge-red text-[10px]"><BookOpen size={10} /> Regulation</span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-text-primary leading-snug">
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener" className="hover:underline">{item.title}</a>
                  ) : item.title}
                </h3>
                <p className="text-xs text-text-secondary mt-1 leading-relaxed line-clamp-2">{item.summary}</p>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-text-muted">
                  <span>{item.source}</span>
                  <span>&middot;</span>
                  <span>{relativeTime(item.timestamp)}</span>
                </div>
              </div>
              {item.url && (
                <a href={item.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary shrink-0">
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <p className="text-xs text-text-muted text-center py-8">No items match your current filters</p>
        )}
      </div>
    </div>
  )
}
